import { Col, Div, Row, Text } from '@pinpoint/design-kit';
import { charts } from '@pinpoint/onpoint-charts';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import CONSTANTS from 'src/store/constants';
import { ApiResponseProp } from 'src/store/typings';
import { formatNumberV2 } from 'src/functions';

import { LabeledCountPanel } from './panel';

export function ApiReturnTimeCardAdvanced({ hist, rttReady, stats, usageFilter }) {
  const [stackedTheme, setStackedTheme] = useState('pinpoint1');

  const chartData = useMemo(
    () => ({
      title: `Return Times Over ${CONSTANTS.REAL_TIME_API.USAGE.FILTERS_DESC[usageFilter]}`,
      series: [
        {
          name: 'count',
          values: hist?.map?.(({ count }) => ({ value: count || null, label: { show: false } })) ?? [],
        },
      ],
      labels: hist?.map?.(({ value }) => value) ?? [],
    }),
    [hist, usageFilter]
  );

  const chartOptions = useMemo(() => {
    const options = {
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
        },
        {
          type: 'inside',
          xAxisIndex: 0,
        },
      ],
      title: {
        top: 0,
      },
      label: {
        position: 'top',
        show: false,
      },
      useUTC: true,
      tooltip: {
        show: true,
        formatter: ({ dataIndex }) => {
          const binRounding = CONSTANTS.REAL_TIME_API.USAGE.BIN_ROUNDING;
          const right = hist[dataIndex].value;
          const left = right - binRounding;
          return `${left} - ${right} ms: ${formatNumberV2(hist[dataIndex].count)} call${
            hist[dataIndex].count > 1 ? 's' : ''
          }`;
        },
      },
      legend: { show: false },
      grid: {
        top: '10%',
        bottom: 112,
      },
      xAxis: {
        name: 'Milliseconds',
        axisTick: true,
        minInterval: 20,
      },
      yAxis: {
        name: 'Number of calls',
        axisTick: true,
        minInterval: 1,
      },
    };
    if (chartData.series.length >= 1)
      options.series = [
        {
          itemStyle: {
            color: '#FFBA00',
          },
        },
      ];
    return options;
  }, [chartData.series.length, hist]);

  return (
    <Row p={{ t: '.75rem', x: '2.5rem', b: '2rem' }} flexGrow="1">
      <Col size="2" d="flex" flexDir="column" justify="flex-start" align="center" gutterWidth="0">
        <Row m={{ b: '1rem' }} w="100%">
          <LabeledCountPanel
            status={rttReady}
            value={`${stats?.p50} ms`}
            title={
              <>
                <Text d="inline-block" textWeight="700" textSize="input">
                  P
                </Text>
                <Text d="inline-block">50</Text>
              </>
            }
            panelProps={{
              p: '1.5rem',
              w: '100%',
            }}
            countProps={{
              textWeight: '500',
              textColor: 'black',
              textSize: 'display1',
            }}
            titleProps={{
              textWeight: '500',
              textSize: 'body',
              textColor: 'marigold',
            }}
          />
        </Row>
        <Row w="100%">
          <LabeledCountPanel
            status={rttReady}
            value={`${stats?.p75} ms`}
            title={
              <>
                <Text d="inline-block" textWeight="700" textSize="input">
                  P
                </Text>
                <Text d="inline-block">75</Text>
              </>
            }
            panelProps={{
              p: '1.5rem',
              w: '100%',
            }}
            countProps={{
              textWeight: '500',
              textColor: 'black',
              textSize: 'display1',
            }}
            titleProps={{
              textWeight: '500',
              textSize: 'body',
              textColor: 'marigold',
            }}
          />
        </Row>
        <Row m={{ t: '1rem' }} w="100%">
          <LabeledCountPanel
            status={rttReady}
            value={`${stats?.p90} ms`}
            title={
              <>
                <Text d="inline-block" textWeight="700" textSize="input">
                  P
                </Text>
                <Text d="inline-block">95</Text>
              </>
            }
            panelProps={{
              p: '1.5rem',
              w: '100%',
            }}
            countProps={{
              textWeight: '500',
              textColor: 'black',
              textSize: 'display1',
            }}
            titleProps={{
              textWeight: '500',
              textSize: 'body',
              textColor: 'marigold',
            }}
          />
        </Row>
      </Col>
      <Col size="10" d="flex" align="stretch" justify="stretch" gutterWidth="0">
        <Row data-debug="chart-row" w="100%" h="100%" m={{ l: '1rem' }}>
          <Col gutterWidth="0" border="1px solid" borderColor="shadeD9" rounded="md" size="12">
            <Div p={{ t: '.5rem', r: '.5rem' }} m={{ b: '-2rem' }} h="100%">
              <charts.VerticalBar
                {...chartData}
                formatter={() => {}}
                options={chartOptions}
                theme={stackedTheme}
                onThemeChange={setStackedTheme}
                usePalette
                showPalette={false}
                markLine={false}
              />
              <Div textAlign="right" textSize="caption" m={{ t: '.5rem' }}>
                Statistics aggregated daily
              </Div>
            </Div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

ApiReturnTimeCardAdvanced.defaultProps = {
  rttReady: null,
};

ApiReturnTimeCardAdvanced.propTypes = {
  // component
  usageFilter: PropTypes.string.isRequired,
  // rtApiUsage store
  rttReady: ApiResponseProp,
  hist: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      right: PropTypes.number.isRequired,
    })
  ).isRequired,
  stats: PropTypes.shape({
    mean: PropTypes.number.isRequired,
    p50: PropTypes.number.isRequired,
    p75: PropTypes.number.isRequired,
    p90: PropTypes.number.isRequired,
  }).isRequired,
};
